import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const KitchenSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [stations, setStations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeTab, setActiveTab] = useState('stations');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchStations(), fetchStaff()]);
    setLoading(false);
  };

  const fetchStations = async () => {
    try {
      const data = await apiGet('/admin/kitchen/setup/stations');
      if (data.success) setStations(data.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const data = await apiGet('/admin/kitchen/setup/staff');
      if (data.success) setStaff(data.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const handleSubmit = async () => {
    const endpoint = activeTab === 'stations' ? 'stations' : 'staff';
    const url = editingItem
      ? `${PRODUCTION_URL}/api/admin/kitchen/setup/${endpoint}/${editingItem.id}`
      : `${PRODUCTION_URL}/api/admin/kitchen/setup/${endpoint}`;

    try {
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ ${activeTab === 'stations' ? 'Station' : 'Staff'} ${editingItem ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        setEditingItem(null);
        setFormData({});
        fetchData();
      }
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const endpoint = type === 'station' ? 'stations' : 'staff';
      const response = await fetch(`${PRODUCTION_URL}/api/admin/kitchen/setup/${endpoint}/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ ${type === 'station' ? 'Station' : 'Staff'} deleted successfully!`);
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const openModal = (item = null, type) => {
    setEditingItem(item);
    setActiveTab(type);

    if (item) {
      setFormData(item);
    } else {
      setFormData(type === 'stations' ? {
        station_name: '',
        station_type: 'hot',
        assigned_staff_id: null,
        is_active: true
      } : {
        staff_name: '',
        role: 'cook',
        phone: '',
        email: '',
        shift: 'morning',
        is_active: true
      });
    }

    setShowModal(true);
  };

  const styles = {
    container: {
      padding: isMobile ? '16px 4px' : '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    tabs: {
      display: 'flex',
      gap: '12px',
      marginBottom: isMobile ? '20px' : '24px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s ease'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: colors.white,
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '12px 8px' : '16px 20px',
      textAlign: 'left',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '700',
      textTransform: 'uppercase'
    },
    td: {
      padding: isMobile ? '12px 8px' : '16px 20px',
      borderBottom: `1px solid ${colors.lightGray}`,
      fontSize: isMobile ? '12px' : '14px'
    },
    button: {
      padding: isMobile ? '6px 10px' : '8px 14px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '600',
      cursor: 'pointer',
      marginRight: '6px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      marginBottom: '12px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      marginBottom: '12px',
      background: colors.white,
      fontFamily: 'inherit'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div>Loading setup...</div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.container}>
        {successMessage && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: colors.success,
            color: colors.white,
            padding: '12px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            {successMessage}
          </div>
        )}

        <h1 style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '700', marginBottom: '6px' }}>
          Kitchen Setup
        </h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight, marginBottom: '24px' }}>
          Configure cooking stations, equipment, and kitchen staff
        </p>

        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              color: activeTab === 'stations' ? colors.primary : colors.textLight,
              borderBottomColor: activeTab === 'stations' ? colors.primary : 'transparent'
            }}
            onClick={() => setActiveTab('stations')}
          >
            🔥 Stations ({stations.length})
          </button>
          <button
            style={{
              ...styles.tab,
              color: activeTab === 'staff' ? colors.primary : colors.textLight,
              borderBottomColor: activeTab === 'staff' ? colors.primary : 'transparent'
            }}
            onClick={() => setActiveTab('staff')}
          >
            👨‍🍳 Staff ({staff.length})
          </button>
        </div>

        {activeTab === 'stations' && (
          <>
            <button
              onClick={() => openModal(null, 'stations')}
              style={{
                ...styles.button,
                background: colors.primary,
                color: colors.white,
                marginBottom: '16px'
              }}
            >
              + Add Station
            </button>

            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Station Name</th>
                    {!isMobile && <th style={styles.th}>Type</th>}
                    <th style={styles.th}>Assigned Staff</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.map(station => (
                    <tr key={station.id}>
                      <td style={styles.td}><strong>{station.station_name}</strong></td>
                      {!isMobile && (
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background: colors.lightGray,
                            fontSize: '11px',
                            textTransform: 'capitalize'
                          }}>
                            {station.station_type}
                          </span>
                        </td>
                      )}
                      <td style={styles.td}>{station.staff_name || 'Unassigned'}</td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: station.is_active ? `${colors.success}20` : `${colors.danger}20`,
                          color: station.is_active ? colors.success : colors.danger
                        }}>
                          {station.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => openModal(station, 'stations')}
                          style={{ ...styles.button, background: colors.accent, color: colors.white }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(station.id, 'station')}
                          style={{ ...styles.button, background: colors.danger, color: colors.white }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'staff' && (
          <>
            <button
              onClick={() => openModal(null, 'staff')}
              style={{
                ...styles.button,
                background: colors.primary,
                color: colors.white,
                marginBottom: '16px'
              }}
            >
              + Add Staff
            </button>

            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Role</th>
                    {!isMobile && <th style={styles.th}>Phone</th>}
                    {!isMobile && <th style={styles.th}>Shift</th>}
                    <th style={styles.th}>Stations</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map(member => (
                    <tr key={member.id}>
                      <td style={styles.td}><strong>{member.staff_name}</strong></td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          background: colors.lightGray,
                          fontSize: '11px',
                          textTransform: 'capitalize'
                        }}>
                          {member.role}
                        </span>
                      </td>
                      {!isMobile && <td style={styles.td}>{member.phone || '-'}</td>}
                      {!isMobile && (
                        <td style={{ ...styles.td, textTransform: 'capitalize' }}>
                          {member.shift}
                        </td>
                      )}
                      <td style={styles.td}>{member.assigned_stations || 0}</td>
                      <td style={styles.td}>
                        <button
                          onClick={() => openModal(member, 'staff')}
                          style={{ ...styles.button, background: colors.accent, color: colors.white }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(member.id, 'staff')}
                          style={{ ...styles.button, background: colors.danger, color: colors.white }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Modal */}
        {showModal && (
          <div style={styles.modal} onClick={() => setShowModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '22px', marginBottom: '20px', fontWeight: '700' }}>
                {editingItem ? 'Edit' : 'Add'} {activeTab === 'stations' ? 'Station' : 'Staff'}
              </h3>

              {activeTab === 'stations' ? (
                <>
                  <label style={styles.label}>Station Name *</label>
                  <input
                    type="text"
                    value={formData.station_name || ''}
                    onChange={(e) => setFormData({ ...formData, station_name: e.target.value })}
                    style={styles.input}
                  />

                  <label style={styles.label}>Station Type *</label>
                  <select
                    value={formData.station_type || 'hot'}
                    onChange={(e) => setFormData({ ...formData, station_type: e.target.value })}
                    style={styles.select}
                  >
                    <option value="hot">Hot Kitchen</option>
                    <option value="cold">Cold Station</option>
                    <option value="grill">Grill Station</option>
                    <option value="pastry">Pastry/Dessert</option>
                    <option value="prep">Prep Station</option>
                  </select>

                  <label style={styles.label}>Assigned Staff</label>
                  <select
                    value={formData.assigned_staff_id || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_staff_id: e.target.value || null })}
                    style={styles.select}
                  >
                    <option value="">Unassigned</option>
                    {staff.filter(s => s.is_active).map(s => (
                      <option key={s.id} value={s.id}>{s.staff_name}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label style={styles.label}>Staff Name *</label>
                  <input
                    type="text"
                    value={formData.staff_name || ''}
                    onChange={(e) => setFormData({ ...formData, staff_name: e.target.value })}
                    style={styles.input}
                  />

                  <label style={styles.label}>Role *</label>
                  <select
                    value={formData.role || 'cook'}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={styles.select}
                  >
                    <option value="chef">Chef</option>
                    <option value="sous_chef">Sous Chef</option>
                    <option value="cook">Cook</option>
                    <option value="helper">Helper</option>
                  </select>

                  <label style={styles.label}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={styles.input}
                  />

                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={styles.input}
                  />

                  <label style={styles.label}>Shift *</label>
                  <select
                    value={formData.shift || 'morning'}
                    onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                    style={styles.select}
                  >
                    <option value="morning">Morning</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                  </select>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={handleSubmit}
                  style={{
                    ...styles.button,
                    background: colors.primary,
                    color: colors.white,
                    flex: 1
                  }}
                >
                  {editingItem ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    ...styles.button,
                    background: colors.mediumGray,
                    color: colors.textDark,
                    flex: 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KitchenSetup;
